/**
 * useMarketingBrandDocumentHead.js — md3c title, favicon, and social image.
 * Shaped by: md3c step 4b; decision R-6.
 * Invariants: when the host is not md3c, this hook does not write document.title
 * or any head tag. fusionmsi.com keeps index.html and applyFusionMsiBranding.
 * Related: resolveMarketingBrandConfig.js, fusionMsiBranding.js,
 * FusionMsiFaviconManager.jsx, FusionMsiMarketingLayout.jsx.
 */
import { useEffect } from 'react';
import { isMd3cMarketingHost } from 'features/md3c/marketing/md3cMarketingRouting';

function ensureLink(selector, create) {
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = create();
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

function ensureMeta(attr, value) {
  const selector = `meta[${attr}="${value}"]`;
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, value);
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

/**
 * @param {{ title?: string, favicon?: { svg?: string, png?: string, apple?: string }, ogImage?: string }} brand
 */
export function useMarketingBrandDocumentHead(brand) {
  const md3cHost = isMd3cMarketingHost();
  const title = brand?.title || '';
  const faviconSvg = brand?.favicon?.svg || '';
  const faviconPng = brand?.favicon?.png || '';
  const appleIcon = brand?.favicon?.apple || '';
  const ogImage = brand?.ogImage || '';

  useEffect(() => {
    if (!md3cHost || typeof document === 'undefined') return undefined;

    const previousTitle = document.title;
    if (title) document.title = title;

    const restores = [];

    if (faviconSvg) {
      const icon = ensureLink('link[rel="icon"]', () => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'icon');
        return link;
      });
      const prevIconHref = icon.el.getAttribute('href');
      const prevIconType = icon.el.getAttribute('type');
      icon.el.setAttribute('type', 'image/svg+xml');
      icon.el.setAttribute('href', faviconSvg);
      restores.push(() => {
        if (icon.created) icon.el.remove();
        else {
          if (prevIconHref != null) icon.el.setAttribute('href', prevIconHref);
          if (prevIconType != null) icon.el.setAttribute('type', prevIconType);
          else icon.el.removeAttribute('type');
        }
      });
    }

    if (faviconPng) {
      const iconPng = ensureLink('link[rel="alternate icon"]', () => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate icon');
        link.setAttribute('type', 'image/png');
        return link;
      });
      const prevIconPngHref = iconPng.el.getAttribute('href');
      iconPng.el.setAttribute('href', faviconPng);
      restores.push(() => {
        if (iconPng.created) iconPng.el.remove();
        else if (prevIconPngHref != null) iconPng.el.setAttribute('href', prevIconPngHref);
      });
    }

    if (appleIcon) {
      const apple = ensureLink('link[rel="apple-touch-icon"]', () => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'apple-touch-icon');
        return link;
      });
      const prevAppleHref = apple.el.getAttribute('href');
      apple.el.setAttribute('href', appleIcon);
      restores.push(() => {
        if (apple.created) apple.el.remove();
        else if (prevAppleHref != null) apple.el.setAttribute('href', prevAppleHref);
      });
    }

    if (ogImage) {
      const socialUrl = ogImage.startsWith('http')
        ? ogImage
        : `${window.location.origin}${ogImage}`;
      const metaPairs = [
        ['property', 'og:image'],
        ['name', 'twitter:image'],
        ['name', 'twitter:card'],
      ];
      const metaValues = {
        'og:image': socialUrl,
        'twitter:image': socialUrl,
        'twitter:card': 'summary_large_image',
      };
      metaPairs.forEach(([attr, key]) => {
        const { el, created } = ensureMeta(attr, key);
        const prevContent = el.getAttribute('content');
        el.setAttribute('content', metaValues[key]);
        restores.push(() => {
          if (created) el.remove();
          else if (prevContent != null) el.setAttribute('content', prevContent);
          else el.removeAttribute('content');
        });
      });
    }

    return () => {
      document.title = previousTitle;
      for (let i = restores.length - 1; i >= 0; i -= 1) restores[i]();
    };
  }, [md3cHost, title, faviconSvg, faviconPng, appleIcon, ogImage]);
}
